import styled from "styled-components";
import { CheckBox, Tooltip } from "@dealer-center/core-components";
import { memo } from "react";
import type { resources } from "@dealer-center/ddc-pages-resources-interface";

interface DashboardResourcesInterface {
  resources: resources[];
  letter: string;
  onChange: (resource: string, enabled: boolean) => void;
  disabled: boolean;
}
const StyledLetterDetailArea = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  & > div {
    box-sizing: border-box;
    min-width: 0;
    @media (max-width: 768px) {
      flex: 0 0 100%;
      max-width: 100%;
    }
    @media (min-width: 768px) {
      flex: 0 0 50%;
      max-width: 50%;
    }
    @media (min-width: 992px) {
      flex: 0 0 33.3%;
      max-width: 33.3%;
    }
  }
`;
const StyledCheckBoxArea = styled.div`
  padding: 0.35em 1.5em 0.35em 0;
  min-width: 0;
  /* Clip a resource name wider than its column at the cell boundary so it can't
     stretch the cell and break the grid alignment. The label carries a title
     attribute so the full name is still available on hover. */
  overflow: hidden;

  /* Single source for resource-name colour: every label in this cell renders
     black, configurable and non-configurable alike (the latter used to be dark
     red). Nested under the wrapper class so it outranks the single emotion class
     Interstate puts on the label. */
  label,
  label * {
    color: #000;
  }
`;
const StyledConfigurableResourceLabel = styled.span``;
const StyledNonConfigurableResourceLabel = styled.span`
  /* Colour comes from StyledCheckBoxArea above; the dashed underline is the
     only remaining visual signal that a resource is locked. */
  border-bottom: 1px dashed #5c0000;
  svg {
    vertical-align: middle;
  }
`;

const LetterDetailArea = memo(
  ({ resources, letter, onChange, disabled }: DashboardResourcesInterface) => {
    return (
      <StyledLetterDetailArea>
        {resources.map(resource => (
          <StyledCheckBoxArea key={resource.name}>
            <CheckBox
              checked={resource.granted}
              disabled={disabled || !resource.configurable}
              id={`dashboard-resources-${letter}-${resource.name}-checkbox`}
              label={
                resource.configurable ? (
                  <StyledConfigurableResourceLabel title={resource.name}>
                    {resource.name}
                  </StyledConfigurableResourceLabel>
                ) : (
                  <StyledNonConfigurableResourceLabel>
                    <Tooltip
                      position="top"
                      size="large"
                      toolTipContent={"Non-Configurable Resource"}
                    >
                      <div>{resource.name}</div>
                    </Tooltip>
                  </StyledNonConfigurableResourceLabel>
                )
              }
              name={resource.name}
              value={`dashboard-resources-${letter}-${resource.name}-checkbox`}
              onChange={event => {
                onChange(resource.name, event.target.value?.checked ?? false);
              }}
            />
          </StyledCheckBoxArea>
        ))}
      </StyledLetterDetailArea>
    );
  },
  (
    prevProps: DashboardResourcesInterface,
    nextProps: DashboardResourcesInterface
  ) => {
    return (
      prevProps.disabled === nextProps.disabled &&
      prevProps.onChange === nextProps.onChange &&
      prevProps.letter === nextProps.letter &&
      prevProps.resources.length === nextProps.resources.length &&
      prevProps.resources.every(
        (resource, index) => resource === nextProps.resources[index]
      )
    );
  }
);

LetterDetailArea.displayName = "LetterDetailArea";

export default LetterDetailArea;
